import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import {
  Shield, Heart, MapPin, Sparkles, CheckCircle2, Coffee, Footprints,
  Dices, MessageCircle, Lock, Siren, UserCheck, Star, ArrowRight, Users,
} from "lucide-react";

const activities = ["Coffee", "Walks", "Board Games", "Events", "Conversation", "Book Clubs", "Art Galleries", "Food Walks"];

const verifiedHosts = [
  { name: "Isha", score: 94, img: "https://images.unsplash.com/photo-1780504862918-4af793b70852?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" },
  { name: "Aarav", score: 98, img: "https://images.unsplash.com/photo-1758561274313-536bf1c4192c?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" },
  { name: "Meera", score: 91, img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" },
  { name: "Rohan", score: 88, img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&q=85&w=500" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const go = () => navigate(user ? "/swipe" : "/register");

  return (
    <div className="bg-sand overflow-hidden">
      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative z-10">
            <span className="inline-flex items-center gap-2 bg-white border border-border rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-terracotta">
              <Sparkles className="w-3.5 h-3.5" /> Bengaluru · Mumbai · Delhi/NCR · 18+
            </span>
            <h1 className="font-display text-5xl md:text-7xl font-black tracking-tight leading-[0.95] text-charcoal mt-6">
              Verified people.<br />
              <span className="text-terracotta italic">Public places.</span><br />
              Real conversations.
            </h1>
            <p className="text-lg text-charcoal/70 mt-6 max-w-md leading-relaxed">
              Saathly is a warm, <strong>strictly platonic</strong> way to meet verified adults for coffee, walks, games and great conversation — always in public, always safe.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button data-testid="hero-cta" onClick={go}
                className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white text-base px-8 h-12 transition-transform duration-200 hover:-translate-y-0.5">
                {user ? "Discover people" : "Join free"} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button variant="outline" data-testid="hero-pricing" onClick={() => navigate(user ? "/packages" : "/register")}
                className="rounded-full border-charcoal/20 text-charcoal hover:bg-white text-base px-8 h-12">
                See packages
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-charcoal/60">
              <span className="flex items-center gap-2"><UserCheck className="w-4 h-4 text-sage" /> KYC verified</span>
              <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-sage" /> Safety suite</span>
              <span className="flex items-center gap-2"><Lock className="w-4 h-4 text-sage" /> No sexual services</span>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4 mt-10">
                <img src="https://images.unsplash.com/photo-1653762378429-8030175fda56?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="Coffee conversation" className="rounded-3xl object-cover h-56 w-full shadow-lg" />
                <div className="bg-sage/15 border border-sage/30 rounded-3xl p-5">
                  <div className="flex items-center gap-2 text-sage font-bold"><CheckCircle2 className="w-5 h-5" /> 98% Trusted</div>
                  <p className="text-sm text-charcoal/70 mt-1">Every host is ID-verified with a trust score.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-terracotta rounded-3xl p-5 text-white">
                  <Users className="w-6 h-6 mb-2" />
                  <p className="font-display text-2xl font-bold">1-3 hr</p>
                  <p className="text-sm text-white/80">timed public hangouts</p>
                </div>
                <img src="https://images.unsplash.com/photo-1691605085147-646fd8059b6d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="Friends walking" className="rounded-3xl object-cover h-56 w-full shadow-lg" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-border bg-white/50 py-5 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...activities, ...activities].map((a, i) => (
            <span key={i} className="font-display italic text-2xl text-charcoal/30 mx-6">{a} <span className="text-terracotta/40">•</span></span>
          ))}
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-charcoal text-center">How a hangout happens</h2>
        <div className="grid md:grid-cols-4 gap-6 mt-12">
          {[
            { icon: UserCheck, t: "Get verified", d: "Phone OTP + government ID & selfie. 18+ only." },
            { icon: Coffee, t: "Find your person", d: "Browse verified hosts by interests, language & distance." },
            { icon: MapPin, t: "Book a public slot", d: "Pick a venue & a 1-3 hour window. First meet capped at 2h." },
            { icon: Star, t: "Meet & review", d: "Check in, enjoy, check out. Rate to build trust." },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-border p-7 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center mb-4">
                <s.icon className="w-6 h-6 text-terracotta" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl font-bold text-charcoal">{s.t}</h3>
              <p className="text-sm text-charcoal/65 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VERIFIED HOSTS STRIP */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-8">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-3xl font-bold text-charcoal">Verified people near you</h2>
          <Link to={user ? "/discover" : "/register"} className="text-terracotta font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all">See all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide">
          {verifiedHosts.map((h) => (
            <div key={h.name} className="min-w-[220px] bg-white rounded-2xl border border-border overflow-hidden shadow-sm">
              <div className="relative h-52">
                <img src={h.img} alt={h.name} className="w-full h-full object-cover" />
                <span className="absolute top-3 right-3 bg-sage text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1"><Shield className="w-3 h-3" /> {h.score}%</span>
              </div>
              <div className="p-4"><p className="font-display text-lg font-bold text-charcoal">{h.name}</p><p className="text-xs text-charcoal/50">Bengaluru</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* SAFETY SUITE */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="bg-charcoal rounded-[2rem] p-8 md:p-14 relative overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-sage">Safety first</span>
              <h2 className="font-display text-3xl md:text-5xl font-bold text-white mt-3 leading-tight">Designed to keep you safe, every single time.</h2>
              <p className="text-white/70 mt-4 leading-relaxed">Public venues only. No sexual services, escorts or private-location meets — ever. Enforced in product, policy and moderation.</p>
              <Button onClick={() => navigate(user ? "/safety" : "/register")} data-testid="safety-cta"
                className="rounded-full bg-sage hover:bg-sage/90 text-white mt-6 px-7 h-11">Explore the safety suite</Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Siren, t: "In-app SOS", d: "One tap alerts our team & your contact." },
                { icon: MapPin, t: "Live location", d: "Share with a trusted contact during a meet." },
                { icon: CheckCircle2, t: "Check-in / out", d: "Timed sessions with reminders." },
                { icon: Shield, t: "Block & report", d: "On every profile, chat and review." },
              ].map((f, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <f.icon className="w-6 h-6 text-sage mb-2" strokeWidth={1.5} />
                  <p className="font-semibold text-white">{f.t}</p>
                  <p className="text-sm text-white/60 mt-1">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI COACH */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-terracotta">AI Conversation & Consent Coach</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-charcoal mt-3">Never run out of things to say.</h2>
            <p className="text-charcoal/70 mt-4 leading-relaxed">Get warm icebreakers, topic ideas and boundary-aware guidance. Our coach refuses anything manipulative or sexual and flags harassment to moderators.</p>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl border border-border p-6 shadow-sm space-y-4">
              <div className="flex gap-3"><div className="w-9 h-9 rounded-full bg-sun flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4 text-charcoal" /></div>
                <div className="bg-sun/20 rounded-2xl rounded-tl-sm p-4 text-sm text-charcoal">Not sure what to say? Ask Isha about her favourite coffee roast — her profile mentions she's a barista on weekends ☕</div></div>
              <div className="flex gap-3 justify-end"><div className="bg-terracotta text-white rounded-2xl rounded-tr-sm p-4 text-sm max-w-xs">Perfect, that's a great opener. Thanks!</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-5 text-center pb-24">
        <Users className="w-10 h-10 text-terracotta mx-auto mb-4" />
        <h2 className="font-display text-4xl md:text-5xl font-black text-charcoal">Real connection starts with trust.</h2>
        <p className="text-charcoal/70 mt-4 text-lg">Join a community of verified adults meeting for genuine, platonic conversations.</p>
        <Button data-testid="bottom-cta" onClick={go} className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white text-base px-10 h-13 mt-8 py-6 transition-transform duration-200 hover:-translate-y-0.5">
          {user ? "Discover people" : "Create your free account"}
        </Button>
      </section>

      <Footer />
    </div>
  );
}
